import { whypeConfig } from '@/config/contracts-generated'
import { getContractAddress } from '@/domain/hooks/useContractAddress'
import { SwapMode, WalletConfig } from './types'
import { PageState } from '@/features/dialogs/common/types'
import { marketBalancesQueryKey } from '@/domain/wallet/marketBalances'
import { queryClient } from '@/config/query-client'
import { hyperEVM } from '@/config/chain/constants'
import { waitForTxReceipt } from '@/utils/waitForTxReceipt'

export interface WrapUnwrap {
  walletConfig: WalletConfig
  chainId: number
  value: bigint
  swapMode: SwapMode
  setSwapState: (state: string) => void
  setSwapError: (error: boolean) => void
  setPageStatus: (status: PageState) => void
}

export async function wrapUnwrap({
  walletConfig,
  chainId,
  value,
  swapMode,
  setSwapState,
  setSwapError,
  setPageStatus,
}: WrapUnwrap): Promise<void> {
  const { account, publicClient, walletClient } = walletConfig

  if (!account || !walletClient || !publicClient) {
    console.warn('Wallet not available or not an Ethereum wallet')
    return
  }

  if (swapMode === 'swap') {
    console.warn('Swap mode not supported')
    return
  }

  try {
    setSwapState('Initializing...')
    setSwapError(false)

    const whypeContractAddress = getContractAddress(whypeConfig.address, chainId)

    if (swapMode === 'wrap') {
      setSwapState('Wrapping...')
      const simulation = await publicClient.simulateContract({
        address: whypeContractAddress,
        account,
        abi: whypeConfig.abi,
        functionName: 'deposit',
        value,
        chain: hyperEVM,
      })

      const hash = await walletClient.writeContract(simulation.request)
      await waitForTxReceipt(publicClient, hash)
    } else {
      setSwapState('Unwrapping...')
      const simulation = await publicClient.simulateContract({
        address: whypeContractAddress,
        account,
        abi: whypeConfig.abi,
        functionName: 'withdraw',
        args: [value],
        chain: hyperEVM,
      })

      const hash = await walletClient.writeContract(simulation.request)
      await waitForTxReceipt(publicClient, hash)
    }

    void queryClient.invalidateQueries({
      queryKey: marketBalancesQueryKey({ account, chainId }),
    })

    setSwapState('')
    setPageStatus('success')
  } catch (error) {
    if (error instanceof Error && error.message.includes('User rejected')) return

    setSwapError(true)
    console.error(error)
  } finally {
    setSwapState('')
  }
}
